import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ApplicationState } from 'store';
import {
  InsuranceCompany,
  INSURANCE_COMPANIES,
  QuoteResponse,
  InsuranceQuote,
  ConlicitasegState,
} from 'store/modules/conlicitaseg_store/types';
import FileIcon from 'assets/images/file_solid.svg';
import PottencialIcon from 'assets/images/pottencial.svg';
import JuntoIcon from 'assets/images/junto.svg';
import SombreroIcon from 'assets/images/sombrero.png';
import AvlaIcon from 'assets/images/avla.png';
import { useHistory } from 'react-router-dom';
import {
  createQuoteAvla,
  createQuoteJunto,
  createQuotePottencial,
  createQuoteSombrero,
  setSecondQuoteTry,
} from 'store/modules/conlicitaseg_store/actions';
import { ProfileState } from 'store/modules/profile_store/types';
import QuoteDownloadBlock from '../quote_download_block';
import { QuoteCard, InsuranceLogo, StateIcon } from './styles';

interface QuotesSelectionCardsProps {
  setSelectedQuote: React.Dispatch<React.SetStateAction<string>>;
  selectedQuote: string;
}

export interface CompanyRequestState {
  loading: boolean;
  loaded: boolean;
  error: boolean;
}

const QuoteSelectionCards = (props: QuotesSelectionCardsProps) => {
  const { setSelectedQuote, selectedQuote } = props;
  const {
    request,
    last_quotes,
    resume_process,
    second_quote_try,
    formProcess,
    current_process_id,
  } = useSelector<ApplicationState, ConlicitasegState>((state) => state.session.conlicitaseg);

  const history = useHistory();
  const dispatch = useDispatch();
  const { profile } = useSelector<ApplicationState, ProfileState>((state) => state.storage.profile);

  const getRequestState = (company: InsuranceCompany) => {
    if (resume_process) {
      return (
        request?.update_quote_by_company?.[company] || {
          loading: false,
          loaded: false,
          error: false,
        }
      );
    }
    return (
      request?.create_quote_by_company?.[company] || { loading: false, loaded: false, error: false }
    );
  };

  const getQuoteByCompany = (company: InsuranceCompany): InsuranceQuote | null => {
    const item = last_quotes?.find(
      (quote: QuoteResponse) =>
        quote?.quote_response?.insurance_company_id?.toLowerCase() === company,
    );
    return item?.quote_response || null;
  };

  const getInsuranceImage = (companyInsurance: string) => {
    switch (companyInsurance?.toLowerCase()) {
      case 'pottencial':
        return PottencialIcon;
      case 'junto':
        return JuntoIcon;
      case 'sombrero':
        return SombreroIcon;
      case 'avla':
        return AvlaIcon;
      default:
        return FileIcon;
    }
  };

  const companyQuotesResponse = INSURANCE_COMPANIES.map((company: InsuranceCompany) => ({
    company,
    quote: getQuoteByCompany(company),
  }));

  const isLoading = INSURANCE_COMPANIES.map(getRequestState).some((state) => state?.loading);
  const hasErrorEveryQuote = INSURANCE_COMPANIES.map(getRequestState).every(
    (state) => state?.error,
  );

  const handleTryQuoteAgain = () => {
    dispatch(setSecondQuoteTry(true));
  };

  useEffect(() => {
    if (!second_quote_try) return;
    if (current_process_id) {
      dispatch(createQuoteJunto(formProcess, profile.id, profile.clientId, current_process_id));
      dispatch(
        createQuotePottencial(formProcess, profile.id, profile.clientId, current_process_id),
      );
      dispatch(createQuoteSombrero(formProcess, profile.id, profile.clientId, current_process_id));
      dispatch(createQuoteAvla(formProcess, profile.id, profile.clientId, current_process_id));
      history.push(`/conlicitaseg/cotacoes/${current_process_id}`);
    }
  }, [second_quote_try]);

  return (
    <>
      {hasErrorEveryQuote && (
        <div className="col-12 mb-4">
          <div className="border rounded p-4 d-flex flex-column text-center align-items-center justify-content-center">
            <StateIcon className="material-icons-outlined text-dark" aria-label="Sem cotações.">
              assignment_late
            </StateIcon>
            <h3>Não foi possível gerar a cotação.</h3>
            {second_quote_try ? (
              <>
                <p>
                  Entre em contato com a nossa equipe de consultores para dar continuidade à sua
                  solicitação.
                </p>
                <a href="/conlicitaseg/contato" className="btn btn-primary">
                  Fale com um consultor
                </a>
              </>
            ) : (
              <>
                <p>Tente refazer a consulta.</p>
                <button type="button" className="btn btn-primary" onClick={handleTryQuoteAgain}>
                  Atualizar cotação
                </button>
              </>
            )}
          </div>
        </div>
      )}
      {!hasErrorEveryQuote && (
        <>
          {companyQuotesResponse.map(({ company, quote }) => {
            if (quote) {
              return (
                <>
                  <div className="col-md-3 mb-4" key={company}>
                    <QuoteCard
                      className={`border rounded p-3 h-100 d-flex flex-column text-center align-items-center justify-content-between ${
                        selectedQuote === quote?.selected_api_quote_id?.toString()
                          ? 'border-primary'
                          : ''
                      } `}
                    >
                      <InsuranceLogo
                        src={getInsuranceImage(quote?.insurance_company_id)}
                        alt="Logo Seguradora"
                      />

                      {quote?.status_id === 'analise' && (
                        <>
                          <div>
                            <div className="font-weight-bold">Documento enviado</div>
                            <div className="text-secondary">Aguardando retorno da seguradora.</div>
                          </div>
                          <button
                            type="button"
                            className="btn btn-outline-primary btn-block"
                            onClick={() => {
                              history.push(
                                `/conlicitaseg/processo/${quote?.insurance_process_id}/${quote?.id}`,
                              );
                            }}
                          >
                            Ver status
                          </button>
                        </>
                      )}
                      {quote?.status_id === 'cotacao' && (
                        <>
                          <div>
                            <div className="font-weight-bold mb-1">Valor do seguro</div>
                            <div className="h4">
                              R${' '}
                              {parseFloat(quote.premium_value).toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </div>
                          </div>
                          <button
                            type="button"
                            className={`btn btn-block ${
                              selectedQuote === quote?.selected_api_quote_id
                                ? 'btn-primary'
                                : 'btn-outline-primary'
                            }`}
                            onClick={() => setSelectedQuote(quote?.selected_api_quote_id)}
                          >
                            {selectedQuote === quote?.selected_api_quote_id
                              ? 'Selecionado'
                              : 'Selecionar'}
                          </button>
                        </>
                      )}
                    </QuoteCard>
                  </div>
                  {selectedQuote === quote?.selected_api_quote_id && (
                    <QuoteDownloadBlock className="col-12 d-md-none mb-4 px-3" quote={quote} />
                  )}
                </>
              );
            }

            return null;
          })}
          {isLoading && (
            <div className="col-12 mb-4 d-flex flex-column align-items-center justify-content-center py-3">
              <div className="spinner-border text-primary" role="status" />
              <div className="text-muted mt-3">Buscando as melhores cotações para você...</div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default QuoteSelectionCards;
